// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { extractUserSegments } from '@iheartjane/menu';
import { ArrowLeftIcon, Flex, Typography } from '@iheartjane/reefer';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';

import {
  HeaderIcons,
  HeaderWithIcons,
  IconButton,
  SignInButton,
} from '@jane/kiosk/components';
import { useKioskUser } from '@jane/kiosk/data-access';
import type { RootLoaderType } from '@jane/kiosk/data-access';
import {
  useKioskCart,
  useKioskSpacing,
  useTypedRouteLoaderData,
} from '@jane/kiosk/hooks';
import { useBrandSpecials, useMenu } from '@jane/kiosk/providers';
import { UnavailableBrandSpecial } from '@jane/shared-ecomm/components';
import { useIsUserInSegment } from '@jane/shared-ecomm/hooks';
import { useGetStore } from '@jane/shared/data-access';
import type { Store } from '@jane/shared/types';

import { BrandSpecialInner } from './BrandSpecialInner';

const SpecialHeader = styled(HeaderWithIcons)(({ theme }) => ({
  backgroundColor: theme.colors.brand.gold.main,
}));

export const BrandSpecial = () => {
  const { isIpad } = useKioskSpacing();
  const { brandSpecialId } = useParams();
  const { pathname, state } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { visibleTabs } = useMenu();

  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const { data: cart } = useKioskCart();
  const { data: kioskUser } = useKioskUser();
  const signedIn = !!kioskUser;
  const showSignIn =
    store?.kiosk_anonymous_checkout !== 'required' && store?.kiosk_allow_auth;

  const { brandSpecials } = useBrandSpecials();
  const currentSpecial = brandSpecials.find(
    ({ id }) => id === Number(brandSpecialId)
  );

  const { includes, excludes } = extractUserSegments({
    brandSpecial: currentSpecial ?? {},
  });
  const userIsEligible = useIsUserInSegment({
    userSegmentIds: kioskUser?.user_segments || [],
    enableCheck: Boolean(currentSpecial && !!kioskUser),
    excludedIds: excludes,
    includedIds: includes,
  });
  const isEligible = useMemo(
    () => userIsEligible && !!currentSpecial,
    [userIsEligible, currentSpecial]
  );

  const previousCategory =
    state?.menuCategory ??
    (visibleTabs.includes('jane-gold') ? 'jane-gold' : 'featured');
  const fromFeatured = previousCategory === 'featured';
  const menuPath = `/stores/${storeId}/menu/${previousCategory}`;

  const navigateToMenu = useCallback(() => {
    navigate(menuPath, {
      state: { resetSearch: true },
    });
  }, [navigate, storeId, menuPath]);

  const navigateToCart = useCallback(() => {
    navigate(`/stores/${storeId}/cart`);
  }, [navigate, storeId]);

  const onSearchIconClick = useCallback(() => {
    navigate(`${pathname}/search`);
  }, [navigate, pathname]);

  const onLoginIconClick = useCallback(() => {
    navigate(`${pathname}/login`);
  }, [navigate, pathname]);

  const onLogoutIconClick = useCallback(() => {
    navigate(`${pathname}/logout`);
  }, [navigate, pathname]);

  const onCartButtonClick = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['cart'],
    });
    navigateToCart();
  }, []);

  return (
    <>
      <SpecialHeader signedIn={signedIn} sticky id="brand-special-page-header">
        <Flex flexDirection="column" width="100%">
          {showSignIn && !signedIn && (
            <SignInButton
              location="brandSpecial"
              ml="auto"
              mb={isIpad ? 16 : 32}
              onLoginIconClick={onLoginIconClick}
            />
          )}
          <Flex alignItems="center" mt={isIpad ? 8 : 16}>
            <IconButton
              ariaLabel="back button"
              onClick={navigateToMenu}
              icon={<ArrowLeftIcon size={isIpad ? 'lg' : 'xl'} />}
            />
            <Typography
              as="h1"
              variant="h1-regular"
              color="grays-black"
              ml={isIpad ? 16 : 32}
              maxLines={2}
              style={{ paddingBottom: '1px', paddingTop: '1px' }}
            >
              Gold{' '}
              {currentSpecial?.special_type === 'product' ? 'reward' : 'bundle'}
            </Typography>
            <Flex grow={1} justifyContent="flex-end">
              <HeaderIcons
                cart={cart}
                onCartButtonClick={onCartButtonClick}
                onLoginIconClick={onLoginIconClick}
                onLogoutIconClick={onLogoutIconClick}
                onSearchIconClick={onSearchIconClick}
                signedIn={signedIn}
                storeId={storeId}
              />
            </Flex>
          </Flex>
        </Flex>
      </SpecialHeader>

      {!isEligible && (
        <UnavailableBrandSpecial
          buttonLabel={
            fromFeatured ? 'Back to menu' : 'Back to Jane Gold rewards'
          }
          backLink={menuPath}
          message="Sorry, you are not eligible for this Jane Gold reward. Return to the menu to explore more cash back options."
          p={isIpad ? 32 : 64}
        />
      )}
      {isEligible && currentSpecial && (
        <BrandSpecialInner
          brandSpecial={currentSpecial}
          store={store}
          navigateToMenu={navigateToMenu}
          navigateToCart={navigateToCart}
        />
      )}
      <Outlet />
    </>
  );
};
